import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { ShoppingCart, X, Plus, Minus, Trash2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useCart, getCartItemKey } from '../context/CartContext';
import { useB2BAuth } from '../context/B2BAuthContext';
import { toast } from 'sonner';
import { calculateB2CShipping } from '../config/shipping';

export const CartDrawer = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, getCartCount, clearCart, isCartOpen, setIsCartOpen } = useCart();
  const { isB2BLoggedIn } = useB2BAuth();
  
  // ONLY use isB2BLoggedIn from auth context - this properly validates the token
  // Do NOT use cartScope as it can be stale from localStorage
  const isB2BMode = isB2BLoggedIn;
  
  const handleClearCart = () => {
    if (window.confirm('Weet je zeker dat je je winkelmandje wilt leegmaken?')) {
      clearCart();
      toast.success('Winkelmandje is leeggemaakt');
    }
  };
  
  const handleContinueShopping = () => {
    setIsCartOpen(false);
    // Navigate to B2B catalog if B2B logged in, otherwise B2C shop
    setTimeout(() => navigate(isB2BMode ? '/b2b/catalog' : '/shop'), 100);
  };
  
  const handleCheckout = () => {
    setIsCartOpen(false);
    // Navigate to B2B cart if B2B logged in, otherwise B2C checkout
    navigate(isB2BMode ? '/b2b/cart' : '/checkout');
  };
  
  // Get item price based on B2B/B2C mode
  const getItemPrice = (item) => {
    if (isB2BMode) {
      return item.b2b_price || item.price || 0;
    }
    return item.price || 0;
  };
  
  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="relative min-w-[44px] min-h-[44px]" data-testid="header-cart-btn">
          <ShoppingCart className="w-5 h-5 text-earth-brown" />
          {getCartCount() > 0 && (
            <span className="absolute -top-1 -right-1 bg-terra-beige text-white text-xs rounded-none w-5 h-5 flex items-center justify-center font-normal" data-testid="header-cart-badge">
              {getCartCount()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden p-4 sm:p-6">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-sweet-sans text-2xl text-earth-brown uppercase tracking-wide">
            Winkelmandje
            {isB2BMode && <span className="text-xs ml-2 text-terra-beige">(B2B)</span>}
          </SheetTitle>
        </SheetHeader>
        
        {/* Success Message Banner */}
        {cart.length > 0 && (
          <div className="mt-4 flex-shrink-0 bg-green-50 border border-green-200  p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-sweet-sans text-green-800 text-sm mb-2">
                  Product toegevoegd aan winkelmandje!
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleContinueShopping}
                    className="border-green-600 text-green-700 hover:bg-green-50 font-sweet-sans text-xs"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" />
                    Verder winkelen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClearCart}
                    className="border-red-500 text-red-600 hover:bg-red-50 font-sweet-sans text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Leegmaken
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCheckout}
                    className="bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet-sans text-xs"
                  >
                    Afrekenen →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-4 sm:mt-6 flex flex-col flex-1 min-h-0">
          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <ShoppingCart className="w-16 h-16 text-terra-beige/30 mb-4" />
              <p className="font-sweet-sans text-almost-black/60">Je winkelmandje is leeg</p>
              <Button 
                onClick={handleContinueShopping}
                className="mt-4 bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet-sans"
              >
                Start met winkelen
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4">
                {cart.map((item) => {
                  // Create unique key (product + variant + personalization)
                  const itemKey = getCartItemKey(item);
                  
                  return (
                  <div key={itemKey} className="flex gap-4 p-4 bg-white ">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-20 h-20 object-cover "
                    />
                    <div className="flex-1">
                      <h3 className="font-sweet-sans text-almost-black text-sm line-clamp-2">
                        {item.name}
                      </h3>
                      {/* Show variant/color info (Phase 2) */}
                      {item.color_name && (
                        <div className="flex items-center gap-1 mt-1">
                          <span 
                            className="w-3 h-3 rounded-full border border-gray-300"
                            style={{ backgroundColor: item.color_hex || '#CCC' }}
                          />
                          <span className="font-sweet-sans text-xs text-terra-beige">{item.color_name}</span>
                        </div>
                      )}
                      {item.personalization && (item.personalization.name || item.personalization.figure) && (
                        <p className="font-sweet-sans text-xs text-terra-beige mt-1" data-testid="cart-item-personalization">
                          Personalisatie: {item.personalization.name}
                          {item.personalization.figure ? ` • ${item.personalization.figure}` : ''}
                          {item.personalization.price ? ` (+€${item.personalization.price.toFixed(2)})` : ''}
                        </p>
                      )}
                      <p className="font-sweet-sans text-xs text-almost-black/60 mb-2">{item.size}</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                          className="w-11 h-11 rounded-none border border-terra-beige/30 flex items-center justify-center hover:bg-terra-beige/10 touch-manipulation"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-sweet-sans text-sm w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                          className="w-11 h-11 rounded-none border border-terra-beige/30 flex items-center justify-center hover:bg-terra-beige/10 touch-manipulation"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeFromCart(itemKey)}
                        className="text-almost-black/40 hover:text-red-600 p-2.5 -mr-2.5 touch-manipulation"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <p className="font-sweet-sans text-lg font-semibold text-earth-brown">
                        €{(getItemPrice(item) * item.quantity).toFixed(2)}
                      </p>
                      {item.original_price && item.discount_percentage > 0 && (
                        <p className="font-sweet-sans text-xs text-gray-400 line-through">
                          €{(item.original_price * item.quantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
              
              <div className="border-t border-terra-beige/20 pt-4 pb-[max(env(safe-area-inset-bottom),0.5rem)] space-y-4 mt-auto shrink-0 bg-white">
                {/* Shipping Message - Subtle, premium styling */}
                {!isB2BMode && (
                  <p className={`text-xs font-sweet-sans tracking-wide text-center ${
                    calculateB2CShipping(getCartTotal()).thresholdReached 
                      ? 'text-stone-600' 
                      : 'text-stone-500'
                  }`} data-testid="cart-shipping-message">
                    {calculateB2CShipping(getCartTotal()).thresholdReached 
                      ? 'Gratis verzending van toepassing'
                      : `Nog €${calculateB2CShipping(getCartTotal()).amountToFree.toFixed(2)} voor gratis verzending`
                    }
                  </p>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="font-sweet-sans text-lg text-earth-brown uppercase tracking-wide">Totaal</span>
                  <span className="font-sweet-sans text-2xl font-bold text-earth-brown">
                    €{getCartTotal().toFixed(2)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet uppercase tracking-wider"
                >
                  Afrekenen
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};